/**
 * 無線 LAN の品質計算（章 4）。
 *
 * 【最重要】ここも出力をハードコードしない。
 * チャンネル・周波数帯・干渉源はすべて NetworkWorld から読み、
 * 実効速度は計算で導く。チャンネルを変えれば速度が変わる。
 *
 * 【乱数を使わない】
 * interferenceRatio / effectiveMbps / recommendChannel は
 * 世界の状態とフラグだけから決まる純粋関数。ping の「揺らぎ」だけが
 * 既存の rng.ts（固定シード方式）を使う。
 *
 * 【技術的な前提】
 * - 2.4GHz は 1〜13ch。各チャンネルは 20MHz 幅で 5MHz 刻みに並ぶため、
 *   チャンネル番号が 5 以上離れて初めて重ならない。1 / 6 / 11 が定番の非重複組。
 * - 5GHz は 20MHz チャンネルが重ならないように定義されているので、
 *   チャンネルが違えば干渉しない。2.4GHz の干渉源とも干渉しない。
 * - 電子レンジは 2.45GHz 付近で強い雑音を出し、2.4GHz 帯のほぼ全域を叩く。
 *   チャンネルを変えても逃げられないので、距離によらない特殊な干渉源として扱う。
 */

import type {
  Device,
  DeviceId,
  InterferenceSource,
  Link,
  NetworkWorld,
} from '../types/network';
import { linksAlongPath } from './topology';

export type WirelessBand = '2.4GHz' | '5GHz';

/** 2.4GHz で使うチャンネル（14ch は 802.11b 専用なので除く） */
export const CHANNELS_24GHZ: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

/** 5GHz の 20MHz チャンネル（W52 / W53 / W56） */
export const CHANNELS_5GHZ: readonly number[] = [
  36, 40, 44, 48, // W52
  52, 56, 60, 64, // W53
  100, 104, 108, 112, 116, 120, 124, 128, 132, 136, 140, // W56
];

/** 2.4GHz の定番の非重複チャンネル。迷ったらここから選ぶ */
export const NON_OVERLAPPING_24GHZ: readonly number[] = [1, 6, 11];

/** 5GHz で最初に選ばれる（DFS の要らない）チャンネル */
export const PREFERRED_5GHZ: readonly number[] = [36, 40, 44, 48];

/**
 * 規格上の速度（Mbps）。
 * 2.4GHz は 802.11n / 20MHz / 2 ストリーム（144Mbps）、
 * 5GHz は 802.11ac / 80MHz / 1 ストリーム（433Mbps）を想定した代表値。
 */
export const NOMINAL_MBPS: Record<WirelessBand, number> = {
  '2.4GHz': 144,
  '5GHz': 433,
};

/** 干渉率の上限。完全に 0 にはならない（電波は届いてはいる） */
export const MAX_INTERFERENCE_RATIO = 0.95;

/** 電子レンジのように帯域全体を叩く干渉源の重なり度（チャンネル距離によらない） */
export const BROADBAND_OVERLAP = 0.9;

/** 帯域全体を叩く干渉源かどうかを label から判定するための語 */
export const BROADBAND_NOISE_KEYWORDS: readonly string[] = [
  '電子レンジ',
  'レンジ',
  'microwave',
];

/** 無線 1 ホップの基本遅延（ms）。干渉ゼロでも有線より少し遅い */
export const WIRELESS_BASE_MS = 2.5;

/** 干渉による遅延の係数（ms）。干渉率の 2 乗で効く */
export const WIRELESS_CONGESTION_MS = 200;

/** これを超える干渉率になると、再送が間に合わずパケットが落ち始める */
export const WIRELESS_LOSS_THRESHOLD = 0.5;

/** 干渉率が上限のときのパケットロス率 */
export const WIRELESS_MAX_LOSS = 0.5;

// ---------------------------------------------------------------
// フラグの受け渡し
// ---------------------------------------------------------------

/**
 * 世界に「いま立っているフラグ」を結びつける。
 *
 * 干渉源の有効・無効は GameState.flags（＝セーブされる進行状態）で持つのが正しい。
 * 一方 ping / traceroute は sim の関数で、GameState を知らない。
 * そこで world から flags を引ける経路をここに 1 つだけ用意する。
 *
 * 保持するのは **参照** なので、applyEffect が state.flags を書き換えれば
 * ここから読める値も同時に変わる（状態の二重管理をしない）。
 * WeakMap なのでテスト間で世界をまたいで漏れることもない。
 */
const BOUND_FLAGS = new WeakMap<NetworkWorld, Record<string, boolean>>();

export function bindWorldFlags(world: NetworkWorld, flags: Record<string, boolean>): void {
  BOUND_FLAGS.set(world, flags);
}

export function worldFlags(world: NetworkWorld): Record<string, boolean> {
  return BOUND_FLAGS.get(world) ?? {};
}

function resolveFlags(
  world: NetworkWorld,
  flags: Record<string, boolean> | undefined,
): Record<string, boolean> {
  return flags ?? worldFlags(world);
}

// ---------------------------------------------------------------
// チャンネルの重なり
// ---------------------------------------------------------------

/**
 * 2.4GHz のチャンネル同士の重なり度（0〜1）。
 *
 * 各チャンネルは 20MHz 幅で 5MHz 間隔に並ぶので、
 * 番号が 5 以上離れれば周波数が重ならない。
 *   距離 0 → 1.0 / 1 → 0.8 / 2 → 0.6 / 3 → 0.4 / 4 → 0.2 / 5 以上 → 0
 * 1 / 6 / 11 はどの組み合わせも距離 5 以上なので 0 になる。
 */
export function channelOverlap(c1: number, c2: number): number {
  if (!Number.isFinite(c1) || !Number.isFinite(c2)) return 0;
  const distance = Math.abs(c1 - c2);
  return Math.max(0, (5 - distance) / 5);
}

/** チャンネル番号からどちらの周波数帯かを判定する。分からなければ null */
export function bandOfChannel(channel: number): WirelessBand | null {
  if (!Number.isFinite(channel)) return null;
  if (channel >= 1 && channel <= 14) return '2.4GHz';
  if (channel >= 32 && channel <= 177) return '5GHz';
  return null;
}

/** その周波数帯で選べるチャンネルの一覧 */
export function channelsForBand(band: WirelessBand): readonly number[] {
  return band === '5GHz' ? CHANNELS_5GHZ : CHANNELS_24GHZ;
}

/** その周波数帯として妥当なチャンネルか */
export function isValidChannel(band: WirelessBand, channel: number): boolean {
  return channelsForBand(band).includes(channel);
}

/** 迷ったら選ぶべき定番チャンネル */
export function preferredChannels(band: WirelessBand): readonly number[] {
  return band === '5GHz' ? PREFERRED_5GHZ : NON_OVERLAPPING_24GHZ;
}

/** 帯域全体を叩く雑音源（電子レンジなど）か */
export function isBroadbandNoise(label: string): boolean {
  const needle = label.toLowerCase();
  return BROADBAND_NOISE_KEYWORDS.some((word) => needle.includes(word.toLowerCase()));
}

/** 干渉源がいま有効か。activeWhenFlag が無ければ常に有効 */
export function isInterferenceActive(
  source: InterferenceSource,
  flags: Record<string, boolean>,
): boolean {
  if (!source.activeWhenFlag) return true;
  return flags[source.activeWhenFlag] === true;
}

/**
 * ある干渉源が、指定の周波数帯・チャンネルにどれだけ重なるか。
 *
 * - 電子レンジ等は 2.4GHz 帯の全域を叩くので、チャンネル距離を見ない。
 *   5GHz には届かないので 0。
 * - 周波数帯が違う干渉源は 0（5GHz の AP は 2.4GHz の干渉を受けない）。
 * - 5GHz の 20MHz チャンネルは重ならないので、一致した時だけ 1。
 */
export function overlapAgainst(
  band: WirelessBand,
  channel: number,
  source: { label: string; channel: number },
): number {
  if (isBroadbandNoise(source.label)) {
    return band === '2.4GHz' ? BROADBAND_OVERLAP : 0;
  }
  if (bandOfChannel(source.channel) !== band) return 0;
  if (band === '5GHz') return source.channel === channel ? 1 : 0;
  return channelOverlap(channel, source.channel);
}

// ---------------------------------------------------------------
// 電波品質
// ---------------------------------------------------------------

/** 干渉の内訳 1 件 */
export interface InterferenceContribution {
  label: string;
  channel: number;
  /** 0〜1。チャンネルの重なり具合 */
  overlap: number;
  /** 0〜100。相手の電波の強さ */
  strength: number;
  /** 自社の AP か、外部の干渉源か */
  kind: 'peer-ap' | 'interference';
}

export interface WirelessQuality {
  apId: DeviceId;
  ssid: string;
  band: WirelessBand;
  channel: number;
  /** 規格上の速度 */
  nominalMbps: number;
  /** 干渉を差し引いた実効速度 */
  effectiveMbps: number;
  /** 0〜0.95 */
  interferenceRatio: number;
  /** 有効な干渉の内訳。強い順 */
  sources: InterferenceContribution[];
}

/**
 * 同じ階にある他の自社 AP を干渉源として拾う。
 *
 * 距離減衰のモデルを持たないので、「同じ階なら電波が届く」という簡略化にする。
 * 別の階の AP は数えない（実際には多少届くが、階をまたいだ強度を
 * 根拠なく決めるより、数えない方が誤りが少ない）。
 * 強度にはその AP の送信出力 txPower をそのまま使う。
 */
function peerAccessPoints(world: NetworkWorld, self: Device): Device[] {
  const floor = world.rooms[self.roomId]?.floor;
  if (floor === undefined) return [];
  return Object.keys(world.devices)
    .map((id) => world.devices[id])
    .filter((device) => {
      if (device.id === self.id) return false;
      if (!device.wireless || !device.powered) return false;
      return world.rooms[device.roomId]?.floor === floor;
    });
}

/** 干渉源（外部＋同じ階の自社 AP）を 1 つのリストにまとめる */
function collectSources(
  world: NetworkWorld,
  device: Device,
  flags: Record<string, boolean>,
): { label: string; channel: number; strength: number; kind: 'peer-ap' | 'interference' }[] {
  const list: { label: string; channel: number; strength: number; kind: 'peer-ap' | 'interference' }[] = [];

  for (const source of device.wireless?.interferenceSources ?? []) {
    if (!isInterferenceActive(source, flags)) continue;
    list.push({
      label: source.label,
      channel: source.channel,
      strength: clamp(source.strength, 0, 100),
      kind: 'interference',
    });
  }

  for (const peer of peerAccessPoints(world, device)) {
    const wireless = peer.wireless;
    if (!wireless) continue;
    list.push({
      label: peer.label,
      channel: wireless.channel,
      strength: clamp(wireless.txPower, 0, 100),
      kind: 'peer-ap',
    });
  }

  return list;
}

function clamp(value: number, min: number, max: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(max, Math.max(min, value));
}

function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** 指定チャンネルで受ける干渉率（0〜MAX_INTERFERENCE_RATIO）と内訳 */
function evaluateChannel(
  band: WirelessBand,
  channel: number,
  sources: { label: string; channel: number; strength: number; kind: 'peer-ap' | 'interference' }[],
): { ratio: number; contributions: InterferenceContribution[] } {
  const contributions: InterferenceContribution[] = [];
  let total = 0;
  for (const source of sources) {
    const overlap = overlapAgainst(band, channel, source);
    if (overlap <= 0 || source.strength <= 0) continue;
    total += overlap * (source.strength / 100);
    contributions.push({
      label: source.label,
      channel: source.channel,
      overlap: round3(overlap),
      strength: source.strength,
      kind: source.kind,
    });
  }
  contributions.sort(
    (a, b) => b.overlap * b.strength - a.overlap * a.strength || a.label.localeCompare(b.label),
  );
  return { ratio: round3(clamp(total, 0, MAX_INTERFERENCE_RATIO)), contributions };
}

/**
 * 無線 AP の電波品質を求める。
 *
 * 無線設定を持たない機器（AP でない）には null を返す。
 * 電源が落ちている AP は電波を出していないので実効速度 0。
 */
export function wirelessQuality(
  world: NetworkWorld,
  apId: DeviceId,
  flags?: Record<string, boolean>,
): WirelessQuality | null {
  const device = world.devices[apId];
  const wireless = device?.wireless;
  if (!device || !wireless) return null;

  const band: WirelessBand = wireless.band === '5GHz' ? '5GHz' : '2.4GHz';
  const nominalMbps = NOMINAL_MBPS[band];

  if (!device.powered) {
    return {
      apId,
      ssid: wireless.ssid,
      band,
      channel: wireless.channel,
      nominalMbps,
      effectiveMbps: 0,
      interferenceRatio: 0,
      sources: [],
    };
  }

  const resolved = resolveFlags(world, flags);
  const sources = collectSources(world, device, resolved);
  const { ratio, contributions } = evaluateChannel(band, wireless.channel, sources);

  return {
    apId,
    ssid: wireless.ssid,
    band,
    channel: wireless.channel,
    nominalMbps,
    effectiveMbps: round1(nominalMbps * (1 - ratio)),
    interferenceRatio: ratio,
    sources: contributions,
  };
}

/** 実効速度だけが欲しい場合（Condition の wirelessQualityAtLeast 用） */
export function effectiveMbps(
  world: NetworkWorld,
  apId: DeviceId,
  flags?: Record<string, boolean>,
): number {
  return wirelessQuality(world, apId, flags)?.effectiveMbps ?? 0;
}

// ---------------------------------------------------------------
// 電波スキャナー用
// ---------------------------------------------------------------

export interface ChannelScan {
  channel: number;
  /** そのチャンネルに居るもの */
  entries: { label: string; strength: number; kind: 'own-ap' | 'peer-ap' | 'interference' }[];
  /** 棒グラフの高さに使う総強度（0〜100 に丸める） */
  totalStrength: number;
  /** この AP がこのチャンネルに移った場合の干渉率 */
  interferenceRatio: number;
  /** いまこの AP が使っているチャンネルか */
  current: boolean;
}

/**
 * チャンネルごとの電波状況。電波スキャナーの棒グラフに使う。
 * 「このチャンネルに変えたら干渉率はいくつになるか」も併せて返すので、
 * UI 側で計算し直す必要がない。
 */
export function scanChannels(
  world: NetworkWorld,
  apId: DeviceId,
  flags?: Record<string, boolean>,
): ChannelScan[] {
  const device = world.devices[apId];
  const wireless = device?.wireless;
  if (!device || !wireless) return [];

  const band: WirelessBand = wireless.band === '5GHz' ? '5GHz' : '2.4GHz';
  const resolved = resolveFlags(world, flags);
  const sources = collectSources(world, device, resolved);

  return channelsForBand(band).map((channel) => {
    const entries: ChannelScan['entries'] = [];
    if (channel === wireless.channel && device.powered) {
      entries.push({
        label: device.label,
        strength: clamp(wireless.txPower, 0, 100),
        kind: 'own-ap',
      });
    }
    for (const source of sources) {
      if (isBroadbandNoise(source.label)) {
        // 電子レンジは帯域全体を叩く。どのチャンネルに逃げても現れる（それがこの回の学び）
        if (band !== '2.4GHz') continue;
        entries.push({
          label: source.label,
          strength: Math.round(source.strength * BROADBAND_OVERLAP),
          kind: source.kind,
        });
        continue;
      }
      if (source.channel !== channel) continue;
      entries.push({ label: source.label, strength: source.strength, kind: source.kind });
    }
    const { ratio } = evaluateChannel(band, channel, sources);
    return {
      channel,
      entries,
      totalStrength: clamp(
        entries.reduce((sum, entry) => sum + entry.strength, 0),
        0,
        100,
      ),
      interferenceRatio: ratio,
      current: channel === wireless.channel,
    };
  });
}

/**
 * いちばん空いているチャンネルを計算で選ぶ。
 *
 * **答えを会話文やデータに書かない。** ここで計算した値を UI が表示する。
 *
 * 並べ替えの優先順位：
 *   1. 干渉率が小さい
 *   2. 定番の非重複チャンネル（2.4GHz なら 1/6/11）である
 *   3. いま使っているチャンネル（同じで良いなら変えない）
 *   4. 番号が小さい
 *
 * 無線設定を持たない機器には 0 を返す（「該当なし」）。
 */
export function recommendChannel(
  world: NetworkWorld,
  apId: DeviceId,
  flags?: Record<string, boolean>,
): number {
  const device = world.devices[apId];
  const wireless = device?.wireless;
  if (!device || !wireless) return 0;

  const band: WirelessBand = wireless.band === '5GHz' ? '5GHz' : '2.4GHz';
  const resolved = resolveFlags(world, flags);
  const sources = collectSources(world, device, resolved);
  const preferred = preferredChannels(band);

  let best = wireless.channel;
  let bestKey: [number, number, number, number] | null = null;

  for (const channel of channelsForBand(band)) {
    const { ratio } = evaluateChannel(band, channel, sources);
    const key: [number, number, number, number] = [
      ratio,
      preferred.includes(channel) ? 0 : 1,
      channel === wireless.channel ? 0 : 1,
      channel,
    ];
    if (bestKey === null || compareKeys(key, bestKey) < 0) {
      bestKey = key;
      best = channel;
    }
  }
  return best;
}

function compareKeys(
  a: [number, number, number, number],
  b: [number, number, number, number],
): number {
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return a[i] - b[i];
  }
  return 0;
}

// ---------------------------------------------------------------
// ping への反映（章 4 の障害は「つながらない」ではなく「遅い」）
// ---------------------------------------------------------------

/** 無線リンクの両端から AP を探す */
export function apOfWirelessLink(world: NetworkWorld, link: Link): DeviceId | null {
  for (const end of ['a', 'b'] as const) {
    const device = world.devices[link[end].deviceId];
    if (device?.wireless) return device.id;
  }
  return null;
}

/**
 * 無線 1 ホップ分の遅延（ms）。
 * 干渉が増えると再送とチャンネルの奪い合いで待たされるため、干渉率の 2 乗で効かせる。
 * 無線設定を持たないリンク（データが未整備）は従来どおり基本遅延だけ。
 */
export function wirelessHopCostMs(
  world: NetworkWorld,
  link: Link,
  flags?: Record<string, boolean>,
): number {
  const apId = apOfWirelessLink(world, link);
  const quality = apId ? wirelessQuality(world, apId, flags) : null;
  if (!quality) return WIRELESS_BASE_MS;
  return WIRELESS_BASE_MS + WIRELESS_CONGESTION_MS * quality.interferenceRatio ** 2;
}

/** 干渉率からパケットロス率を導く。閾値までは落ちない */
export function lossRateForRatio(ratio: number): number {
  if (ratio <= WIRELESS_LOSS_THRESHOLD) return 0;
  const excess = (ratio - WIRELESS_LOSS_THRESHOLD) / (1 - WIRELESS_LOSS_THRESHOLD);
  return round3(clamp(excess * WIRELESS_MAX_LOSS, 0, WIRELESS_MAX_LOSS));
}

/**
 * 経路上の無線リンクから決まるパケットロス率。
 * 複数あれば最も悪いものを採る。
 */
export function wirelessLossRateForPath(
  world: NetworkWorld,
  path: DeviceId[],
  flags?: Record<string, boolean>,
): number {
  if (path.length <= 1) return 0;
  let worst = 0;
  for (const linkId of linksAlongPath(world, path)) {
    const link = world.links[linkId];
    if (!link?.wireless) continue;
    const apId = apOfWirelessLink(world, link);
    const quality = apId ? wirelessQuality(world, apId, flags) : null;
    if (!quality) continue;
    worst = Math.max(worst, lossRateForRatio(quality.interferenceRatio));
  }
  return worst;
}
